/* eslint-disable */
// ─────────────────────────────────────────────────────────────────────────────
// Audit-log humanizer.
//
// Turns the raw data we already store (method + URL + entity + before/after) into
// readable lines like "Approved a booking request #1A2B3C4D" and compact field
// diffs like "Seats: 2 → 4".
//
// OPEN BY DESIGN: every lookup below is *polish only* and falls back to a sane
// derived default. A brand-new endpoint/entity/table renders sensibly with zero
// edits here — you only touch these maps when you want nicer wording.
// ─────────────────────────────────────────────────────────────────────────────

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// URL/path prefixes that aren't the real subject of the action.
const PREFIXES = new Set(["api", "v1", "customer", "admin", "dashboard"]);

// Friendly singular label per resource segment. Fallback = de-pluralize + de-kebab.
const ENTITY_LABELS = {
  reservations: "reservation",
  "booking-requests": "booking request",
  requests: "booking request",
  users: "user",
  trips: "trip",
  schedules: "schedule",
  pricing: "price override",
  boat: "deck",
  decks: "deck",
  wallet: "wallet",
  wallets: "wallet",
  payments: "payment",
  refunds: "refund",
  promocode: "promo code",
  promocodes: "promo code",
  tickets: "ticket",
  roles: "role",
  permissions: "permission",
  reports: "report",
  auth: "account",
};

// Trailing URL verb → { label (past tense), tone }.
const VERB_LABELS = {
  approve: { label: "Approved", tone: "positive" },
  reject: { label: "Rejected", tone: "danger" },
  complete: { label: "Completed", tone: "positive" },
  cancel: { label: "Cancelled", tone: "danger" },
  "cancel-request": { label: "Requested cancellation of", tone: "warning" },
  "change-request": { label: "Requested change to", tone: "warning" },
  refund: { label: "Refunded", tone: "warning" },
  restore: { label: "Restored", tone: "positive" },
  activate: { label: "Activated", tone: "positive" },
  deactivate: { label: "Deactivated", tone: "warning" },
  checkin: { label: "Checked in", tone: "positive" },
  "check-in": { label: "Checked in", tone: "positive" },
  scan: { label: "Scanned", tone: "neutral" },
  adjust: { label: "Adjusted", tone: "warning" },
  checkout: { label: "Paid for", tone: "positive" },
  "pay-difference": { label: "Paid difference on", tone: "positive" },
  "simulate-success": { label: "Marked paid (dev)", tone: "neutral" },
  enable: { label: "Enabled", tone: "positive" },
  disable: { label: "Disabled", tone: "warning" },
  verify: { label: "Verified", tone: "neutral" },
  setup: { label: "Set up", tone: "neutral" },
  permanent: { label: "Permanently deleted", tone: "danger" },
  profile: { label: "Updated", tone: "neutral" },
  validate: { label: "Validated", tone: "neutral" },
};

// HTTP method → { label, tone } when there's no explicit URL verb.
const METHOD_VERBS = {
  POST: { label: "Created", tone: "positive" },
  PATCH: { label: "Updated", tone: "neutral" },
  PUT: { label: "Updated", tone: "neutral" },
  DELETE: { label: "Deleted", tone: "danger" },
};

// Pretty labels for diff fields. Fallback = de-camelCase the key.
const FIELD_LABELS = {
  adultSeats: "Adults",
  childSeats: "Children",
  deckId: "Deck",
  tripDateId: "Trip date",
  tripId: "Trip",
  totalPrice: "Total",
  status: "Status",
  balance: "Wallet balance",
  isActive: "Active",
  isCancelled: "Cancelled",
  isCheckedIn: "Checked in",
  role: "Role",
  firstName: "First name",
  lastName: "Last name",
  guestName: "Guest name",
  guestEmail: "Guest email",
  computedRefund: "Refund",
  computedDifference: "Difference",
  adminNote: "Admin note",
  customerNote: "Customer note",
};

// Override the auto text for specific actions. Key = "METHOD entity/verb".
// Optional polish only — the app works fully without any entries here. Each value
// may set { label, noun, tone } or a fully-formed { text, tone }.
const ACTION_OVERRIDES = {
  // Security-sensitive auth actions read better with explicit wording.
  "POST auth/enable": { label: "Enabled", noun: "two-factor auth", tone: "positive" },
  "POST auth/disable": { label: "Disabled", noun: "two-factor auth", tone: "warning" },
  "POST auth/setup": { label: "Set up", noun: "two-factor auth", tone: "neutral" },
  "POST auth/verify": { label: "Verified", noun: "two-factor auth", tone: "neutral" },
  "POST auth/logout": { text: "Logged out", tone: "neutral" },
};

// ── helpers ──────────────────────────────────────────────────────────────────

const isUuidish = (s) =>
  UUID_RE.test(s) || /^\d+$/.test(s) || /^[0-9a-f]{16,}$/i.test(s);

const deKebab = (s) => s.replace(/[-_]+/g, " ");

const deCamel = (s) =>
  s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const entityLabel = (seg) =>
  ENTITY_LABELS[seg] ||
  deKebab(seg).replace(/s$/, "") || // naive de-pluralize
  seg;

const fieldLabel = (key) => FIELD_LABELS[key] || deCamel(key);

const idSuffix = (id) =>
  id ? ` #${String(id).replace(/-/g, "").slice(0, 8).toUpperCase()}` : "";

// ── humanizeAction ───────────────────────────────────────────────────────────

/**
 * @param {{action?:string, method?:string, entityType?:string, entityId?:string,
 *          statusCode?:number}} row
 * @returns {{ text: string, tone: 'positive'|'neutral'|'warning'|'danger' }}
 */
export function humanizeAction(row = {}) {
  const method = (row.method || "").toUpperCase();

  // Path without the leading method + api/v1 prefix; keep real ids for parsing.
  const path = String(row.action || "")
    .replace(/^[A-Z]+\s+/, "")
    .replace(/^\/+/, "")
    .split("?")[0];
  const segments = path.split("/").filter(Boolean);

  // Skip known prefixes to find the real subject.
  const meaningful = segments.filter((s) => !PREFIXES.has(s));

  // Entity = first non-id meaningful segment.
  const entitySeg =
    meaningful.find((s) => !isUuidish(s)) || row.entityType || "record";

  // Verb = last non-id segment that isn't the entity itself.
  const tail = [...meaningful].reverse();
  const verbSeg = tail.find((s) => !isUuidish(s) && s !== entitySeg);

  const override = ACTION_OVERRIDES[`${method} ${entitySeg}/${verbSeg || ""}`];

  let label, tone;
  if (override) {
    label = override.label;
    tone = override.tone || "neutral";
  } else if (verbSeg && VERB_LABELS[verbSeg]) {
    ({ label, tone } = VERB_LABELS[verbSeg]);
  } else if (verbSeg && !METHOD_VERBS[method]) {
    // Unknown but present verb (future endpoint) — humanize it directly.
    label = deCamel(deKebab(verbSeg));
    tone = "neutral";
  } else {
    ({ label, tone } = METHOD_VERBS[method] || {
      label: method || "Action",
      tone: "neutral",
    });
  }

  const noun = override?.noun ?? entityLabel(entitySeg);
  const id = row.entityId || meaningful.find((s) => isUuidish(s));

  let text = override?.text || `${label} ${noun}${idSuffix(id)}`.trim();

  if (row.statusCode != null && row.statusCode >= 400) {
    tone = "danger";
  }

  return { text, tone };
}

// ── humanizeDiff ─────────────────────────────────────────────────────────────

const NOISE_KEYS = new Set(["id", "createdAt", "updatedAt", "deletedAt"]);

const fmtValue = (v) => {
  if (v == null || v === "") return "—";
  if (v === "***") return "•••";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 60 ? s.slice(0, 57) + "…" : s;
  }
  const s = String(v);
  // Numeric strings (decimals from the DB) → grouped.
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s).toLocaleString();
  return s.length > 80 ? s.slice(0, 77) + "…" : s;
};

/**
 * @returns {{ mode:'diff'|'create'|'empty', rows: Array<{label,from,to}|{label,value}> }}
 */
export function humanizeDiff(before, after) {
  const b = before && typeof before === "object" ? before : null;
  const a = after && typeof after === "object" ? after : null;

  // Update: diff the two snapshots.
  if (b && a) {
    const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
    const rows = [];
    for (const key of keys) {
      if (NOISE_KEYS.has(key)) continue;
      const from = b[key];
      const to = a[key];
      if (JSON.stringify(from) === JSON.stringify(to)) continue;
      rows.push({ label: fieldLabel(key), from: fmtValue(from), to: fmtValue(to) });
    }
    return rows.length ? { mode: "diff", rows } : { mode: "empty", rows: [] };
  }

  // Create / payload only: show a compact summary of notable fields.
  if (a) {
    const rows = [];
    for (const key of Object.keys(a)) {
      if (NOISE_KEYS.has(key)) continue;
      const val = a[key];
      if (val == null || val === "" || typeof val === "object") continue;
      rows.push({ label: fieldLabel(key), value: fmtValue(val) });
    }
    return rows.length ? { mode: "create", rows } : { mode: "empty", rows: [] };
  }

  return { mode: "empty", rows: [] };
}

// Tone → Ant Design Tag color.
export const toneColor = (tone) =>
  ({ positive: "green", neutral: "blue", warning: "gold", danger: "red" }[tone] ||
  "default");
